import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  CheckCheck,
  CircleDashed,
  CircleHelp,
  Copy,
  ExternalLink,
  Landmark,
  MoreHorizontal,
  NotebookPen,
  Pencil,
  Target,
  Trash2,
  Undo2,
  User,
  type LucideIcon,
} from 'lucide-react'
import type { AccountType } from '@/api/accounts'
import type { TransactionSummary } from '@/api/transactions'
import { useListAmountFormat } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import { useIsReadOnly } from '@/hooks/useIsReadOnly'
import { Button } from '@/components/ui/button'
import { MENU_SURFACE } from '@/components/ui/menu-surface'
import { CategoryIcon } from '@/lib/categoryIcon'
import { cn } from '@/lib/utils'
import {
  actionsFor,
  rowLinks,
  type AmountTone,
  type RowLink,
  type GlyphName,
  type RowModel,
  type TagTone,
  type TileTone,
} from './rowModel'

// --- Row actions — one "more" trigger opening a menu: changes (edit, duplicate, refund),
// "Open …" links to what the row involves, then Delete. ---

interface MenuPosition {
  top?: number
  bottom?: number
  right: number
}

const ITEM_CLS =
  'h-auto w-full justify-start gap-2.5 rounded-none px-3 py-2 text-sm font-normal text-[var(--fl-text-1)]'

export function RowActions({
  onEdit,
  onDuplicate,
  onDelete,
  onRefund,
  onReconcile,
  reconciled = false,
  links = [],
  labelSuffix = '',
}: {
  onEdit?: () => void
  onDuplicate?: () => void
  onDelete?: () => void
  onRefund?: () => void
  onReconcile?: () => void
  reconciled?: boolean
  links?: RowLink[]
  labelSuffix?: string
}) {
  const isReadOnly = useIsReadOnly()
  const [open, setOpen] = useState(false)
  // Rendered in a portal (see below) so this is never clipped by a row's scrolling ancestor
  // (a rounded card's `overflow-hidden`, …) — position is computed fresh each time it opens,
  // fixed relative to the viewport, the only boundary a portal can still be clipped by.
  const [position, setPosition] = useState<MenuPosition | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleOutside(e: MouseEvent) {
      const target = e.target as Node
      if (wrapperRef.current?.contains(target)) return
      if (popoverRef.current?.contains(target)) return
      setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [open])

  // Read-only members can still follow the links; every other item changes data.
  const changes = isReadOnly
    ? []
    : [
        onEdit && {
          key: 'edit',
          title: `Edit${labelSuffix}`,
          label: 'Edit',
          Icon: Pencil,
          run: onEdit,
        },
        onDuplicate && {
          key: 'duplicate',
          title: 'Duplicate',
          label: 'Duplicate',
          Icon: Copy,
          run: onDuplicate,
        },
        onRefund && {
          key: 'refund',
          title: 'Record Refund',
          label: 'Record Refund',
          Icon: Undo2,
          run: onRefund,
        },
        onReconcile && {
          key: 'reconcile',
          title: reconciled ? 'Mark not reconciled' : 'Mark reconciled',
          label: reconciled ? 'Mark not reconciled' : 'Mark reconciled',
          Icon: reconciled ? CircleDashed : CheckCheck,
          run: onReconcile,
        },
      ].filter(
        (
          x
        ): x is { key: string; title: string; label: string; Icon: LucideIcon; run: () => void } =>
          !!x
      )
  const canDelete = !isReadOnly && !!onDelete
  const itemCount = changes.length + links.length + (canDelete ? 1 : 0)
  if (itemCount === 0) return null

  function toggleOpen() {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const estimatedMenuHeight = itemCount * 38 + 24
      const openUpward = window.innerHeight - rect.bottom < estimatedMenuHeight
      setPosition({
        ...(openUpward ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
        right: window.innerWidth - rect.right,
      })
    }
    setOpen((o) => !o)
  }

  const divider = <div role="separator" className="my-1 h-px bg-[var(--fl-divider)]" />

  return (
    <div className="relative shrink-0" ref={wrapperRef}>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="icon-sm"
        onClick={toggleOpen}
        aria-expanded={open}
        aria-haspopup="menu"
        tooltip={`Actions${labelSuffix}`}
        className="text-[var(--fl-text-3)]"
      >
        <MoreHorizontal size={16} />
      </Button>
      {open &&
        position &&
        createPortal(
          <div
            ref={popoverRef}
            role="menu"
            style={{
              position: 'fixed',
              top: position.top,
              bottom: position.bottom,
              right: position.right,
            }}
            className={`${MENU_SURFACE} z-50 w-60 py-1`}
          >
            {changes.map((c) => (
              <Button
                key={c.key}
                variant="ghost"
                role="menuitem"
                title={c.title}
                onClick={() => {
                  setOpen(false)
                  c.run()
                }}
                className={cn(ITEM_CLS, 'hover:bg-[var(--fl-surface-hover)]')}
              >
                <c.Icon size={14} /> {c.label}
              </Button>
            ))}
            {links.length > 0 && changes.length > 0 && divider}
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                role="menuitem"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-3 py-2 text-sm text-[var(--fl-text-1)] hover:bg-[var(--fl-surface-hover)]"
              >
                <ExternalLink size={14} aria-hidden="true" className="shrink-0" />
                <span className="truncate">{l.label}</span>
              </Link>
            ))}
            {canDelete && (changes.length > 0 || links.length > 0) && divider}
            {canDelete && (
              <Button
                variant="ghost"
                role="menuitem"
                title={`Delete${labelSuffix}`}
                onClick={() => {
                  setOpen(false)
                  onDelete!()
                }}
                className={cn(
                  ITEM_CLS,
                  'hover:bg-[var(--fl-error-bg)] hover:text-[var(--fl-error)]'
                )}
              >
                <Trash2 size={14} /> Delete
              </Button>
            )}
          </div>,
          document.body
        )}
    </div>
  )
}

const GLYPHS: Record<GlyphName, LucideIcon> = {
  move: ArrowLeftRight,
  in: ArrowDownLeft,
  out: ArrowUpRight,
  goal: Target,
  person: User,
  refund: Undo2,
  loan: Landmark,
  unknown: CircleHelp,
}

const TILE_CLS: Record<TileTone, string> = {
  spend: 'bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)]',
  in: 'bg-[var(--fl-primary-subtle)] text-[var(--fl-primary-text)]',
  move: 'bg-[var(--fl-warning-bg)] text-[var(--fl-warning-dark)]',
  warn: 'border-[1.5px] border-dashed border-[var(--fl-warning-text)] text-[var(--fl-warning-text)]',
}

const AMOUNT_CLS: Record<AmountTone, string> = {
  out: 'text-[var(--fl-expense)]',
  in: 'text-[var(--fl-income)]',
  neutral: 'text-[var(--fl-text-1)]',
  void: 'text-[var(--fl-text-3)] line-through',
}

const TAG_CLS: Record<TagTone, string> = {
  neutral: 'bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)]',
  green: 'bg-[var(--fl-primary-subtle)] text-[var(--fl-primary-text)]',
  amber: 'bg-[var(--fl-warning-bg)] text-[var(--fl-warning-dark)]',
}

const SUB_CLS = {
  green: 'text-[var(--fl-income)]',
  amber: 'text-[var(--fl-warning-text)]',
  muted: 'text-[var(--fl-text-2)]',
}

export function TransactionRow({
  row,
  accountTypes = new Map(),
  onEdit,
  onDuplicate,
  onDelete,
  onRefund,
  onReconcile,
  selection,
}: {
  row: RowModel
  /** Select mode: the whole row toggles its checkbox and the actions menu is hidden. */
  selection?: { checked: boolean; onToggle: () => void }
  /** Decides whether an account link goes to the account or to its loan page. */
  accountTypes?: Map<string, AccountType>
  onEdit?: (tx: TransactionSummary) => void
  onDuplicate?: (tx: TransactionSummary) => void
  onDelete?: (tx: TransactionSummary) => void
  onRefund?: (tx: TransactionSummary) => void
  onReconcile?: (row: RowModel) => void
}) {
  const format = useListAmountFormat()
  const wide = useMediaQuery(DESKTOP_QUERY)
  const { canEdit, canDelete, canRefund, canDuplicate } = actionsFor(row)
  const Glyph = row.icon.kind === 'glyph' ? GLYPHS[row.icon.name] : null

  const Root = selection ? 'label' : 'div'

  return (
    <Root
      className={cn(
        'group flex items-start gap-2.5 px-3 py-2.5 transition-colors hover:bg-[var(--fl-surface-hover)] md:gap-3 md:px-4 md:py-3',
        selection && 'cursor-pointer',
        selection?.checked && 'bg-[var(--fl-primary-faint)]'
      )}
    >
      {selection && (
        <input
          type="checkbox"
          aria-label={`Select ${row.title}`}
          checked={selection.checked}
          onChange={selection.onToggle}
          className="mt-2 h-[18px] w-[18px] shrink-0 cursor-pointer accent-[var(--fl-primary)] md:mt-3"
        />
      )}
      <span
        aria-hidden="true"
        className={cn(
          'grid h-8 w-8 shrink-0 place-items-center rounded-[10px] md:h-10 md:w-10 md:rounded-xl',
          TILE_CLS[row.tile]
        )}
      >
        {Glyph ? (
          <Glyph strokeWidth={1.8} className="size-4 md:size-[19px]" />
        ) : (
          <CategoryIcon
            icon={row.icon.kind === 'category' ? row.icon.name : ''}
            size={wide ? 19 : 16}
          />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-3">
          <p className="min-w-0 flex-1 truncate text-sm font-medium text-[var(--fl-text-1)] md:text-[15px]">
            {row.title}
          </p>
          <p
            data-testid="row-amount"
            className={cn(
              'shrink-0 whitespace-nowrap font-serif text-[15px] font-semibold tabular-nums md:text-[17px]',
              AMOUNT_CLS[row.tone]
            )}
          >
            {row.reconciled && (
              <CheckCheck
                size={14}
                role="img"
                aria-label="Reconciled"
                className="mr-1 inline align-[-1px] text-[var(--fl-primary)]"
              />
            )}
            {row.sign}
            {format(row.amount)}
          </p>
        </div>
        <div className="mt-0.5 flex items-baseline gap-3">
          <p
            className={cn(
              'min-w-0 flex-1 truncate text-xs md:text-[13px]',
              row.metaWarn ? 'text-[var(--fl-warning-text)]' : 'text-[var(--fl-text-2)]'
            )}
          >
            {row.meta}
          </p>
          {row.sub && (
            <p
              className={cn(
                'shrink-0 whitespace-nowrap text-xs font-medium',
                SUB_CLS[row.sub.tone]
              )}
            >
              {row.sub.text}
            </p>
          )}
        </div>
        {row.note && (
          <p className="mt-0.5 flex items-center gap-1.5 text-[12.5px] italic text-[var(--fl-text-2)]">
            <NotebookPen size={12} aria-hidden="true" className="shrink-0" />
            <span className="truncate">{row.note}</span>
          </p>
        )}
        {row.tags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {row.tags.map((tag) => (
              <span
                key={tag.label}
                className={cn(
                  'rounded-full px-2 py-1 text-[11.5px] font-medium leading-none',
                  TAG_CLS[tag.tone]
                )}
              >
                {tag.label}
              </span>
            ))}
          </div>
        )}
      </div>

      {!selection && (
        <div className="md:mt-1 md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:focus-within:opacity-100">
          <RowActions
            onEdit={canEdit && onEdit ? () => onEdit(row.tx) : undefined}
            onDuplicate={canDuplicate && onDuplicate ? () => onDuplicate(row.tx) : undefined}
            onDelete={canDelete && onDelete ? () => onDelete(row.tx) : undefined}
            onRefund={canRefund && onRefund ? () => onRefund(row.tx) : undefined}
            onReconcile={onReconcile ? () => onReconcile(row) : undefined}
            reconciled={row.reconciled}
            links={rowLinks(row, accountTypes)}
            labelSuffix={row.kind === 'transfer' ? ' transfer' : ''}
          />
        </div>
      )}
    </Root>
  )
}
